// Copied from personalization/lib/ink-contrast.js — keep in sync.
// personalization/lib/ink-contrast.js
//
// Which personalization ink colours a garment may offer (Grant, 2026-09-25):
// "we essentially don't want a blue on blue". An ink is HIDDEN when it is in
// the same colour family as the garment, or too close to it to read.
//
// ONE rule, run in four places from byte copies of this file:
//   - the storefront "Make it yours" modal (shopify-theme-premium-white/assets/
//     gf-ink-contrast.js) decides which swatches to show;
//   - orderwebhook (lib/ink-contrast.js) refuses a cart line whose ink the modal
//     would not have offered, so a crafted cart cannot order it;
//   - teamstore-createproducts-v4 (lib/ink-contrast.js) stamps the same list
//     into the product blob at create time.
// Each copy is this file under a "Copied from" header line.
//
// Zero dependencies. Runs unmodified in Node (module.exports) and in the
// browser (window.GfInkContrast).
(function (factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') window.GfInkContrast = api;
})(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Thresholds. Hue and chroma are OKLCH (hue in degrees, chroma 0..~0.37);
  // lightness is OKLab L (0 black .. 1 white).
  // ---------------------------------------------------------------------------
  // Two chromatic colours whose hues are within this many degrees are one
  // family whatever their lightness: navy / royal / columbia are all blue.
  const HUE_FAMILY_DEG = 30;
  // Below this chroma a colour reads as a neutral (black, greys, white).
  const MIN_CHROMA = 0.04;
  // Neutral lightness bands. Dark: black, charcoal, dark heather, anthracite.
  // Light: white, ash, light grey, silver. Anything between is a middle grey,
  // which blocks any neutral within NEUTRAL_NEIGHBOUR_L of it.
  const NEUTRAL_DARK_MAX_L = 0.50;
  const NEUTRAL_LIGHT_MIN_L = 0.80;
  const NEUTRAL_NEIGHBOUR_L = 0.20;
  // Extra checks for pairs the family test lets through (e.g. navy ink on a
  // black garment, yellow on white): perceptual distance and luminance contrast.
  const MIN_DELTA_E = 25; // CIEDE2000
  const MIN_CONTRAST = 1.5; // WCAG relative-luminance ratio
  // A NEUTRAL ink (black / white) on a COLOURED garment skips the ΔE floor when
  // it reads at least this well (Grant 2026-10-07: black on Duck Brown, ΔE 24.0
  // but 2.24:1). Colour inks and neutral garments keep the floor.
  const NEUTRAL_INK_MIN_CONTRAST = 2.2;

  // ---------------------------------------------------------------------------
  // Colour maths
  // ---------------------------------------------------------------------------
  function normalizeHex(hex) {
    let h = String(hex == null ? '' : hex).trim().replace(/^#/, '');
    if (/^[0-9a-fA-F]{3}$/.test(h)) h = h.split('').map((c) => c + c).join('');
    if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
    return '#' + h.toUpperCase();
  }

  function rgb(hex) {
    const h = normalizeHex(hex);
    if (!h) return null;
    return [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16) / 255);
  }

  function linear(c) {
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  }

  function relativeLuminance(hex) {
    const c = rgb(hex);
    if (!c) return null;
    const [r, g, b] = c.map(linear);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }

  function contrastRatio(a, b) {
    const la = relativeLuminance(a);
    const lb = relativeLuminance(b);
    if (la == null || lb == null) return null;
    const hi = Math.max(la, lb);
    const lo = Math.min(la, lb);
    return (hi + 0.05) / (lo + 0.05);
  }

  function oklch(hex) {
    const c = rgb(hex);
    if (!c) return null;
    const [r, g, b] = c.map(linear);
    const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
    const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
    const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
    const L = 0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s;
    const A = 1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s;
    const B = 0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s;
    const C = Math.sqrt(A * A + B * B);
    let H = Math.atan2(B, A) * 180 / Math.PI;
    if (H < 0) H += 360;
    return { L, C, H };
  }

  function lab(hex) {
    const c = rgb(hex);
    if (!c) return null;
    const [r, g, b] = c.map(linear);
    // sRGB -> XYZ (D65), normalised to the D65 white.
    const x = (0.4124564 * r + 0.3575761 * g + 0.1804375 * b) / 0.95047;
    const y = (0.2126729 * r + 0.7151522 * g + 0.0721750 * b) / 1.0;
    const z = (0.0193339 * r + 0.1191920 * g + 0.9503041 * b) / 1.08883;
    const f = (t) => (t > 216 / 24389 ? Math.cbrt(t) : (24389 / 27 * t + 16) / 116);
    const fx = f(x); const fy = f(y); const fz = f(z);
    return { L: 116 * fy - 16, a: 500 * (fx - fy), b: 200 * (fy - fz) };
  }

  // CIEDE2000 (Sharma, Wu & Dalal 2005), kL = kC = kH = 1.
  function deltaE2000(hexA, hexB) {
    const p = lab(hexA);
    const q = lab(hexB);
    if (!p || !q) return null;
    return deltaE2000Lab(p, q);
  }

  function deltaE2000Lab(p, q) {
    const rad = Math.PI / 180;
    const C1 = Math.hypot(p.a, p.b);
    const C2 = Math.hypot(q.a, q.b);
    const Cbar = (C1 + C2) / 2;
    const G = 0.5 * (1 - Math.sqrt(Math.pow(Cbar, 7) / (Math.pow(Cbar, 7) + Math.pow(25, 7))));
    const a1 = (1 + G) * p.a;
    const a2 = (1 + G) * q.a;
    const C1p = Math.hypot(a1, p.b);
    const C2p = Math.hypot(a2, q.b);
    const hp = (a, b) => {
      if (a === 0 && b === 0) return 0;
      const h = Math.atan2(b, a) / rad;
      return h < 0 ? h + 360 : h;
    };
    const h1 = hp(a1, p.b);
    const h2 = hp(a2, q.b);
    const dL = q.L - p.L;
    const dC = C2p - C1p;
    let dh = 0;
    if (C1p * C2p !== 0) {
      dh = h2 - h1;
      if (dh > 180) dh -= 360;
      else if (dh < -180) dh += 360;
    }
    const dH = 2 * Math.sqrt(C1p * C2p) * Math.sin((dh / 2) * rad);
    const Lbar = (p.L + q.L) / 2;
    const Cbarp = (C1p + C2p) / 2;
    let hbar = h1 + h2;
    if (C1p * C2p !== 0) {
      if (Math.abs(h1 - h2) <= 180) hbar = (h1 + h2) / 2;
      else hbar = (h1 + h2 + (h1 + h2 < 360 ? 360 : -360)) / 2;
    }
    const T = 1 - 0.17 * Math.cos((hbar - 30) * rad) + 0.24 * Math.cos(2 * hbar * rad)
      + 0.32 * Math.cos((3 * hbar + 6) * rad) - 0.20 * Math.cos((4 * hbar - 63) * rad);
    const dTheta = 30 * Math.exp(-Math.pow((hbar - 275) / 25, 2));
    const RC = 2 * Math.sqrt(Math.pow(Cbarp, 7) / (Math.pow(Cbarp, 7) + Math.pow(25, 7)));
    const SL = 1 + (0.015 * Math.pow(Lbar - 50, 2)) / Math.sqrt(20 + Math.pow(Lbar - 50, 2));
    const SC = 1 + 0.045 * Cbarp;
    const SH = 1 + 0.015 * Cbarp * T;
    const RT = -Math.sin(2 * dTheta * rad) * RC;
    return Math.sqrt(
      Math.pow(dL / SL, 2) + Math.pow(dC / SC, 2) + Math.pow(dH / SH, 2) + RT * (dC / SC) * (dH / SH),
    );
  }

  function hueDistance(a, b) {
    const d = Math.abs(a - b) % 360;
    return d > 180 ? 360 - d : d;
  }

  function neutralBand(L) {
    if (L <= NEUTRAL_DARK_MAX_L) return 'dark';
    if (L >= NEUTRAL_LIGHT_MIN_L) return 'light';
    return 'mid';
  }

  /**
   * Why `inkHex` should NOT be offered on `garmentHex`, or null when it may.
   * Reasons: 'same-hue-family', 'same-neutral-band', 'too-similar' (ΔE),
   * 'low-contrast'. An unreadable hex on either side returns null — the caller
   * decides what an unknown garment means (see filterInks).
   */
  function inkConflict(inkHex, garmentHex) {
    const ink = oklch(inkHex);
    const garment = oklch(garmentHex);
    if (!ink || !garment) return null;
    const inkChromatic = ink.C >= MIN_CHROMA;
    const garmentChromatic = garment.C >= MIN_CHROMA;
    if (inkChromatic && garmentChromatic && hueDistance(ink.H, garment.H) <= HUE_FAMILY_DEG) {
      return 'same-hue-family';
    }
    if (!inkChromatic && !garmentChromatic) {
      const a = neutralBand(ink.L);
      const b = neutralBand(garment.L);
      if (a === b && a !== 'mid') return 'same-neutral-band';
      if ((a === 'mid' || b === 'mid') && Math.abs(ink.L - garment.L) < NEUTRAL_NEIGHBOUR_L) {
        return 'same-neutral-band';
      }
    }
    const contrast = contrastRatio(inkHex, garmentHex);
    const neutralInkReads = !inkChromatic && garmentChromatic && contrast >= NEUTRAL_INK_MIN_CONTRAST;
    if (!neutralInkReads && deltaE2000(inkHex, garmentHex) < MIN_DELTA_E) return 'too-similar';
    if (contrast < MIN_CONTRAST) return 'low-contrast';
    return null;
  }

  function inkAllowedOn(inkHex, garmentHex) {
    return inkConflict(inkHex, garmentHex) == null;
  }

  /**
   * choices: [{ id, hex }] in display order. Returns the ids to offer, in the
   * same order. Every choice that passes inkConflict is kept. When none pass,
   * the single highest-contrast choice is kept (ties: larger ΔE, then order),
   * so a garment always offers at least one ink. An unknown garment (no usable
   * hex) filters nothing. Choices with an unreadable hex are dropped unless
   * nothing else is left.
   */
  function filterInks(choices, garmentHex) {
    const list = (Array.isArray(choices) ? choices : []).filter((c) => c && c.id != null);
    if (!list.length) return [];
    if (!normalizeHex(garmentHex)) return list.map((c) => c.id);
    const readable = list.filter((c) => normalizeHex(c.hex));
    if (!readable.length) return list.map((c) => c.id);
    const kept = readable.filter((c) => inkAllowedOn(c.hex, garmentHex));
    if (kept.length) return kept.map((c) => c.id);
    let best = null;
    for (const c of readable) {
      const score = [contrastRatio(c.hex, garmentHex), deltaE2000(c.hex, garmentHex)];
      if (!best || score[0] > best.score[0] + 1e-9
        || (Math.abs(score[0] - best.score[0]) <= 1e-9 && score[1] > best.score[1])) {
        best = { id: c.id, score };
      }
    }
    return [best.id];
  }

  /**
   * True when `inkHex` is one the modal would offer on `garmentHex` given the
   * same `choices`. Compares by colour, not id: the cart carries the resolved
   * hex. Server-side check for a submitted cart line.
   */
  function inkOffered(inkHex, garmentHex, choices) {
    const ink = normalizeHex(inkHex);
    if (!ink) return false;
    const list = (Array.isArray(choices) ? choices : []).filter((c) => c && normalizeHex(c.hex));
    const allowedIds = filterInks(list, garmentHex);
    return list.some((c) => allowedIds.indexOf(c.id) !== -1 && normalizeHex(c.hex) === ink);
  }

  // ---------------------------------------------------------------------------
  // Garment colour from its NAME. Team-store colourways are separate products
  // and carry only a colour name (theme.sibling_color), so the modal and the
  // server resolve the SAME name through the SAME table. These are family hue
  // hints, not brand swatches: good enough to decide "is this a blue", never to
  // feed an image prompt. Values follow teamstore-createproducts-v4/lib/
  // fabric-match-hex.js HUE_HINTS plus the theme's gf-color-hex palette.
  // Longest keyword wins; a multi-colour name ("Black/ Gold") uses its first
  // segment (the garment body).
  // ---------------------------------------------------------------------------
  const GARMENT_NAME_HINTS = [
    // greens
    ['forest green', '#014421'], ['kelly green', '#00853E'], ['irish green', '#009A44'],
    ['dark green', '#023020'], ['gorge green', '#2C5234'], ['collegiate green', '#0F3D2E'],
    ['sport green', '#00A650'], ['safety green', '#00FF00'], ['neon green', '#39FF14'],
    ['olive', '#6B8E23'], ['forest', '#014421'], ['kelly', '#00853E'], ['green', '#1F7A44'],
    // blues
    ['midnight navy', '#101820'], ['collegiate navy', '#001F3F'], ['college navy', '#001F3F'],
    ['true navy', '#12244A'], ['heather navy', '#2A3B57'], ['navy', '#0B1F3A'],
    ['game royal', '#0033A0'], ['true royal', '#0033A0'], ['team royal', '#0033A0'],
    ['collegiate royal', '#0033A0'], ['royal', '#0B4EA2'],
    ['carolina blue', '#4B9CD3'], ['columbia blue', '#7BAFD4'], ['light blue', '#7BAFD4'],
    ['sky blue', '#87CEEB'], ['valor blue', '#274B75'], ['steel blue', '#4682B4'],
    ['sapphire', '#0F52BA'], ['aqua', '#00B5C0'], ['teal', '#00827F'],
    ['carolina', '#4B9CD3'], ['columbia', '#7BAFD4'], ['blue', '#1F4E9C'],
    // reds / maroons / pinks
    ['university red', '#C8102E'], ['power red', '#DA291C'], ['scarlet', '#B00020'],
    ['true red', '#C8102E'], ['team red', '#C8102E'], ['deep red', '#8B0000'],
    ['cardinal', '#9D2235'], ['team maroon', '#6F263D'], ['maroon', '#6F263D'],
    ['burgundy', '#6D2135'], ['crimson', '#9E1B32'], ['sangria', '#8E2F5C'],
    ['coral', '#FF6F5E'], ['pink', '#E86A92'], ['red', '#C8102E'],
    // oranges / golds / yellows
    ['texas orange', '#BF5700'], ['burnt orange', '#BF5700'], ['tennessee orange', '#FF8200'],
    ['safety orange', '#FF6900'], ['orange', '#F26522'],
    ['vegas gold', '#C5B358'], ['old gold', '#CFB53B'], ['athletic gold', '#FFB81C'],
    ['university gold', '#FFB81C'], ['gold', '#FFB81C'], ['maize', '#FFCB05'],
    ['safety yellow', '#E9FF32'], ['neon yellow', '#DFFF00'], ['yellow', '#FFD500'],
    // purples
    ['court purple', '#582C83'], ['team purple', '#582C83'], ['lavender', '#B57EDC'],
    ['violet', '#7F00FF'], ['purple', '#582C83'],
    // browns / naturals
    ['desert khaki', '#C3B091'], ['khaki', '#C3B091'], ['tan', '#C8AD7F'], ['sand', '#DCD3C3'],
    ['natural', '#F1E9D2'], ['cream', '#F1E9D2'], ['oatmeal', '#D9CFC1'], ['ivory', '#FFFFF0'],
    ['chocolate', '#4A2C2A'], ['brown', '#5C4033'],
    // neutrals (last: a hue word anywhere in the name wins first)
    ['jet black', '#000000'], ['black heather', '#2B2B2B'], ['anthracite', '#36393B'],
    ['graphite', '#3B3B3B'], ['charcoal', '#36454F'], ['iron grey', '#5A5A5A'],
    ['iron gray', '#5A5A5A'], ['dark smoke heather', '#4E5052'], ['dark smoke grey', '#4E5052'],
    ['dark heather grey', '#565656'], ['dark grey heather', '#565656'], ['dark heather', '#565656'],
    ['dark grey', '#555555'], ['dark gray', '#555555'], ['smoke', '#4E5052'],
    ['heather grey', '#A5A5A5'], ['heather gray', '#A5A5A5'], ['grey heather', '#A5A5A5'],
    ['gray heather', '#A5A5A5'], ['athletic heather', '#A5A5A5'],
    ['sport grey', '#9E9E9E'], ['sport gray', '#9E9E9E'], ['athletic grey', '#919191'],
    ['oxford', '#8C8C8C'], ['grey three', '#9EA2A2'], ['flint grey', '#7A7D80'],
    ['light grey', '#D3D3D3'], ['light gray', '#D3D3D3'], ['ash', '#E0E0DC'],
    ['silver', '#C0C0C0'], ['black', '#1D1D1F'], ['white', '#FFFFFF'],
    ['grey', '#919191'], ['gray', '#919191'],
  ].sort((a, b) => b[0].length - a[0].length);

  function garmentHexFromName(name) {
    const raw = String(name == null ? '' : name).trim().toLowerCase();
    if (!raw) return null;
    const segment = raw.split('/')[0].trim();
    for (const source of [segment, raw]) {
      for (let i = 0; i < GARMENT_NAME_HINTS.length; i += 1) {
        const needle = GARMENT_NAME_HINTS[i][0];
        const re = new RegExp('(^|[^a-z])' + needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '($|[^a-z])');
        if (re.test(source)) return GARMENT_NAME_HINTS[i][1];
      }
    }
    return null;
  }

  // Placeholder hexes that mean "no real swatch": SanMar batch-import writes
  // #888888 for colours it has no hex for; the theme's gf-color-hex snippet
  // falls back to #cccccc.
  const PLACEHOLDER_HEXES = ['#888888', '#CCCCCC'];

  /**
   * The garment hex to test inks against: a real swatch hex when one is given
   * (not a placeholder), else the name's family hint, else null (unknown).
   */
  function resolveGarmentHex(garment) {
    const g = garment || {};
    const explicit = normalizeHex(g.hex);
    if (explicit && PLACEHOLDER_HEXES.indexOf(explicit) === -1) return explicit;
    return normalizeHex(garmentHexFromName(g.name));
  }

  return {
    HUE_FAMILY_DEG,
    MIN_CHROMA,
    NEUTRAL_DARK_MAX_L,
    NEUTRAL_LIGHT_MIN_L,
    NEUTRAL_NEIGHBOUR_L,
    MIN_DELTA_E,
    MIN_CONTRAST,
    NEUTRAL_INK_MIN_CONTRAST,
    normalizeHex,
    relativeLuminance,
    contrastRatio,
    oklch,
    deltaE2000,
    deltaE2000Lab,
    inkConflict,
    inkAllowedOn,
    filterInks,
    inkOffered,
    garmentHexFromName,
    resolveGarmentHex,
  };
});
